import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ListAlt } from '@mui/icons-material';
import { competitorBacklinksService, CompetitorBacklink } from '../services/competitorBacklinksService';
import { useAuth } from '../contexts/AuthContext';

const GUEST_BLOG_ARRIVED_COUNT_KEY = ['guest-blog-arrived-request-count'] as const;

function extractReferringKey(url: string): string {
  if (!url) return '';
  try {
    const raw = url.trim();
    const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(withProto).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return url
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0]
      .trim();
  }
}

/** One row per referring domain; keeps highest DR; merges “new in catalog” across duplicate requests. */
type AggregatedRequestRow = CompetitorBacklink & {
  mergedSourceIds: string[];
  unseenCatalogArrival: boolean;
};

/** Guest Blog Sites list: filter by this hostname + optional cart project context */
function browseGuestSitesHref(domain: string, projectId?: string): string {
  const params = new URLSearchParams();
  params.set('search', domain);
  params.set('from', 'requested-sites');
  if (projectId) params.set('projectId', projectId);
  return `/sites?${params.toString()}`;
}

function aggregateByReferringDomain(rows: CompetitorBacklink[]): AggregatedRequestRow[] {
  const groups = new Map<string, CompetitorBacklink[]>();
  for (const r of rows) {
    const rawKey = r.referringDomain || extractReferringKey(r.backlinkUrl) || r.id;
    const k = (rawKey || r.id).toLowerCase();
    const list = groups.get(k);
    if (list) list.push(r);
    else groups.set(k, [r]);
  }

  const merged: AggregatedRequestRow[] = [];
  for (const group of Array.from(groups.values())) {
    const sorted = [...group].sort((a, b) => {
      const drA = a.backlinkDr ?? -1;
      const drB = b.backlinkDr ?? -1;
      if (drB !== drA) return drB - drA;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    const best = sorted[0];
    const drs = group
      .map((g: CompetitorBacklink) => g.backlinkDr)
      .filter((x: number | undefined): x is number => typeof x === 'number');
    const maxDr = drs.length > 0 ? Math.max(...drs) : best.backlinkDr;
    const inGuestBlogCatalog = group.some((g: CompetitorBacklink) => g.inGuestBlogCatalog);
    const unseenCatalogArrival = group.some(
      (g: CompetitorBacklink) => g.inGuestBlogCatalog && !g.guestBlogMatchSeenAt
    );
    const mergedSourceIds = group.map((g: CompetitorBacklink) => g.id);

    merged.push({
      ...best,
      backlinkDr: maxDr,
      inGuestBlogCatalog,
      mergedSourceIds,
      unseenCatalogArrival,
    });
  }

  merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return merged;
}

const ClientRequestedSitesPage: React.FC = () => {
  const { isClient } = useAuth();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<CompetitorBacklink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    if (!isClient) return;
    try {
      setLoading(true);
      setError('');
      const data = await competitorBacklinksService.getAllRequestedBacklinks();
      setRows(data);
    } catch (e) {
      console.error(e);
      setError('Could not load your requested sites.');
    } finally {
      setLoading(false);
    }
  }, [isClient]);

  useEffect(() => {
    if (isClient) void load();
  }, [isClient, load]);

  const displayRows = useMemo(() => aggregateByReferringDomain(rows), [rows]);

  const unseenArrivedIds = useMemo(
    () =>
      displayRows
        .filter((r) => r.unseenCatalogArrival)
        .flatMap((r) => r.mergedSourceIds),
    [displayRows]
  );

  const unseenUniqueDomainCount = useMemo(
    () => displayRows.filter((r) => r.unseenCatalogArrival).length,
    [displayRows]
  );

  const handleDismissArrivedNotice = async () => {
    if (unseenArrivedIds.length === 0) return;
    try {
      setDismissing(true);
      setError('');
      await competitorBacklinksService.markGuestBlogMatchesSeen(unseenArrivedIds);
      void queryClient.invalidateQueries({ queryKey: GUEST_BLOG_ARRIVED_COUNT_KEY });
      await load();
    } catch (e: unknown) {
      console.error(e);
      const ax = e as { response?: { data?: { message?: string } } };
      const serverMsg = ax.response?.data?.message;
      setError(serverMsg || 'Could not update notifications. Try again.');
    } finally {
      setDismissing(false);
    }
  };

  if (!isClient) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ListAlt color="primary" />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Requested sites
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Referring domains you requested from the{' '}
        <Link component={RouterLink} to="/competitor-backlink-analysis" underline="hover">
          Competitor Backlink Analysis
        </Link>
        . Each domain is listed once (multiple page-level requests are combined). When a site is added to{' '}
        <Link component={RouterLink} to="/sites" underline="hover">
          Guest Blog Sites
        </Link>
        , you will see it here and get a sidebar alert until you dismiss it.
      </Typography>

      {unseenUniqueDomainCount > 0 && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" disabled={dismissing} onClick={() => void handleDismissArrivedNotice()}>
              {dismissing ? <CircularProgress size={18} color="inherit" /> : 'Dismiss alert'}
            </Button>
          }
        >
          {unseenUniqueDomainCount === 1
            ? 'One requested site is now available in Guest Blog Sites.'
            : `${unseenUniqueDomainCount} requested sites are now available in Guest Blog Sites.`}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            You have not requested any sites from the DR Calculator yet.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Requested domain</TableCell>
                <TableCell align="right">DR</TableCell>
                <TableCell>Competitor context</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row) => {
                const domain = row.referringDomain || extractReferringKey(row.backlinkUrl);
                const showNew = row.unseenCatalogArrival;
                const mergedCount = row.mergedSourceIds.length;
                return (
                  <TableRow key={domain || row.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {domain}
                      </Typography>
                      {mergedCount > 1 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {mergedCount} requests combined (highest DR shown)
                        </Typography>
                      )}
                      {row.backlinkTitle && mergedCount === 1 && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap title={row.backlinkTitle}>
                          {row.backlinkTitle}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{row.backlinkDr ?? '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.competitorWebsite}</Typography>
                    </TableCell>
                    <TableCell>{row.project?.projectName ?? '—'}</TableCell>
                    <TableCell>
                      {row.inGuestBlogCatalog ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                          <Chip size="small" color="success" label="In Guest Blog Sites" />
                          {showNew && <Chip size="small" color="warning" variant="outlined" label="New" />}
                          <Link
                            component={RouterLink}
                            to={browseGuestSitesHref(domain, row.project?.id)}
                            variant="caption"
                            underline="hover"
                          >
                            Browse sites
                          </Link>
                        </Box>
                      ) : (
                        <Chip size="small" variant="outlined" label="Awaiting catalog" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClientRequestedSitesPage;
